import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import Button from '../common/Button.js'
import {
  cancelRequestedByLabel,
  cancelledByLabel,
} from '../../utils/shipmentCancel.js'
import { formatIndianDate, formatIndianTime } from '../../utils/localDate.js'

const TITLES = {
  request: 'Cancel shipment',
  approve: 'Approve cancellation',
  reject: 'Reject cancel request',
  view: 'Cancellation details',
}

const SUBMIT_LABELS = {
  request: 'Submit',
  approve: 'Approve cancel',
  reject: 'Reject request',
}

const CancelShipmentModal = ({
  open,
  mode = 'request',
  shipment,
  submitting = false,
  onClose,
  onSubmit,
}) => {
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (open) {
      setReason(mode === 'approve' ? (shipment?.cancel_reason || '') : '')
    }
  }, [open, mode, shipment])

  if (!open || !shipment) return null

  const readOnly = mode === 'view'
  const needsReason = mode === 'request' || mode === 'approve'
  const reasonOk = !needsReason || String(reason).trim().length >= 3

  const handleSubmit = (event) => {
    event.preventDefault()
    if (readOnly) {
      onClose?.()
      return
    }
    if (needsReason && !reasonOk) return
    onSubmit?.(String(reason).trim())
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{TITLES[mode] || TITLES.request}</h3>
            <p className="text-xs text-gray-500 mt-0.5">GC {shipment.shipment_id}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-lg text-gray-500 hover:bg-gray-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {mode === 'view' ? (
            <>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase">Reason</p>
                <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{shipment.cancel_reason || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase">Cancelled by</p>
                <p className="text-sm text-gray-800 mt-1">{cancelledByLabel(shipment)}</p>
              </div>
              {shipment.cancelled_at && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">Cancelled at</p>
                  <p className="text-sm text-gray-800 mt-1">
                    {formatIndianDate(shipment.cancelled_at)}
                    {formatIndianTime(shipment.cancelled_at) ? ` · ${formatIndianTime(shipment.cancelled_at)}` : ''}
                  </p>
                </div>
              )}
              {shipment.cancel_requested_by && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">Requested by</p>
                  <p className="text-sm text-gray-800 mt-1">{cancelRequestedByLabel(shipment)}</p>
                </div>
              )}
            </>
          ) : (
            <>
              {mode !== 'request' && shipment.cancel_reason && (
                <p className="text-sm text-gray-600">
                  Requested reason: <span className="font-medium text-gray-800">{shipment.cancel_reason}</span>
                </p>
              )}
              {needsReason ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cancellation reason
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Enter the reason (min 3 characters)"
                    required
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Optional note for the agent"
                  />
                </div>
              )}
            </>
          )}
          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {mode === 'view' ? 'Close' : 'Back'}
            </Button>
            {mode !== 'view' && (
              <Button
                type="submit"
                variant={mode === 'reject' ? 'secondary' : 'primary'}
                className="flex-1"
                loading={submitting}
                disabled={submitting || (needsReason && !reasonOk)}
              >
                {SUBMIT_LABELS[mode] || 'Submit'}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default CancelShipmentModal
